import { useState, useCallback, useMemo } from 'react';

import { useGetLedgers, LedgerFetchFilter, AccountListItem } from '../../api/accounts';
import { useAppContext } from '../../contexts/Storage';

interface UseLedgersOptions {
  recordId?: number;
}

const useLedgers = (options: UseLedgersOptions = {}) => {
  const { recordId: initialRecordId } = options;
  const { user, selectedRecord } = useAppContext();

  // Use provided recordId or fall back to selected record
  const recordId = initialRecordId ?? selectedRecord?.id;
  const userId = user?.id;

  // Filter state
  const [filters, setFilters] = useState<Partial<LedgerFetchFilter>>({
    per_page: 15,
    page: 1,
  });

  // Search state (client-side filtering)
  const [searchTerm, setSearchTerm] = useState('');

  // Selected ledger for detail view
  const [selectedLedger, setSelectedLedger] = useState<AccountListItem | null>(null);

  // API hook - only fetch when we have record_id and user_id
  const hasRequiredParams = !!recordId && !!userId;
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetLedgers({
    filters: {
      ...filters,
      record_id: recordId,
      user_id: userId,
    },
    enabled: hasRequiredParams,
  });

  // Derived data
  const ledgers = useMemo(() => data?.ledgers ?? [], [data?.ledgers]);
  const pagination = useMemo(() => data?.pagination, [data?.pagination]);

  // Client-side filtered ledgers (for search)
  const filteredLedgers = useMemo(() => {
    if (!searchTerm.trim()) return ledgers;

    const term = searchTerm.toLowerCase();
    return ledgers.filter(
      (ledger) =>
        ledger.transaction_entity_name?.toLowerCase().includes(term) ||
        ledger.transaction_entity_description?.toLowerCase().includes(term) ||
        ledger.transaction_entity_category?.toLowerCase().includes(term)
    );
  }, [ledgers, searchTerm]);

  // Filter handlers
  const updateFilter = useCallback((updates: Partial<LedgerFetchFilter>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleDateFilter = useCallback((startDate?: string, endDate?: string) => {
    updateFilter({
      start_date: startDate,
      end_date: endDate,
      page: 1,
    });
  }, [updateFilter]);

  const handlePageChange = useCallback((page: number) => {
    updateFilter({ page });
  }, [updateFilter]);

  const handlePerPageChange = useCallback((perPage: number) => {
    updateFilter({ per_page: perPage, page: 1 });
  }, [updateFilter]);

  const resetFilters = useCallback(() => {
    setFilters({ per_page: 15, page: 1 });
    setSearchTerm('');
  }, []);

  // Ledger selection
  const handleSelectLedger = useCallback((ledger: AccountListItem) => {
    setSelectedLedger(ledger);
  }, []);

  const handleCloseLedgerDetail = useCallback(() => {
    setSelectedLedger(null);
  }, []);

  // Map ledgers to ListItem format for the template
  const listItems = useMemo(() => {
    return filteredLedgers.map((ledger) => ({
      id: ledger.id ?? 0,
      name: ledger.transaction_entity_name ?? '',
      description: ledger.transaction_entity_description ?? '',
      category: ledger.transaction_entity_category ?? '',
      type: ledger.transaction_entity_type ?? '',
      balance: ledger.transaction_entity_balance_amount
        ? `$${ledger.transaction_entity_balance_amount.toLocaleString()}`
        : undefined,
      balanceType: ledger.transaction_entity_balance_type,
      balanceQuantity: ledger.transaction_entity_balance_quantity,
    }));
  }, [filteredLedgers]);

  return {
    // Data
    ledgers,
    filteredLedgers,
    listItems,
    pagination,
    selectedLedger,

    // Loading states
    isLoading,
    hasRequiredParams,

    // Error state
    error,

    // Filter state and handlers
    filters,
    searchTerm,
    handleSearchChange,
    handleDateFilter,
    handlePageChange,
    handlePerPageChange,
    resetFilters,
    updateFilter,

    // Ledger actions
    handleSelectLedger,
    handleCloseLedgerDetail,

    // Refetch
    refetch,
  };
};

export default useLedgers;
